import React, { FC, useState } from 'react'
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { IllustrationDiscount } from '@moonpig/launchpad-assets'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  MULTI_CARD_DISCOUNTS,
  SHOW_MULTI_CARD_DISCOUNTS_TAG,
} from '@moonpig/web-core-brand/config'
import { Region } from '@moonpig/web-core-types'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { ModuleContext, Module, ModuleTabs, Tab as TabType } from '../types'
import { PageTypeEnum } from '../../types'
import { useProductsLocaleText } from '../../locale'

const StyledTabs = styled(Tabs)`
  [role="tablist"] {
    ${s({
      justifyContent: 'flex-start',
      gap: 12,
      paddingX: 4,
      paddingTop: 7,
      marginBottom: { xs: 0, md: 3 },
    })}
  }
`

const StyledTabPanels = styled(TabPanels)`
  background-color: inherit;
`

const StyledInfoStrip = styled.div<{
  selected: boolean
  isMobile: boolean
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: -58%;
  right: 16%;
  width: 68%;
  border-radius: 8px 8px 0 0;
  height: 24px;
  ${({ selected }: { selected?: boolean }) =>
    s({
      paddingX: selected ? '7px' : 4,
    })}
`

const PromoTabTrigger = styled(Tab)`
  position: relative;

  &:hover ${StyledInfoStrip} {
    height: 22px;
  }

  &[aria-selected='true']:hover ${StyledInfoStrip} {
    height: 24px;
  }
`

type Props = {
  module: ModuleTabs
  context: ModuleContext
  mapModule: (module: Module, props: ModuleContext) => React.JSX.Element | null
  onChanged?: (tab: TabType, index: number) => void
  'data-testid'?: string
}

const getShowDiscountTag = (storeId: Region) =>
  MULTI_CARD_DISCOUNTS[storeId].length > 0 && SHOW_MULTI_CARD_DISCOUNTS_TAG

export const TabsModule: FC<Props> = ({
  module: { tabs },
  context,
  mapModule,
  onChanged,
  'data-testid': testId = 'module-tabs',
}) => {
  const [selectedValue, setSelectedValue] = useState(() => tabs[0]?.title)
  const localiseText = useProductsLocaleText()
  const storeId = useStoreId()
  const isMobile = useIsMobile()
  const hasDiscounts = getShowDiscountTag(storeId)
  const hasTitleAboveTabs = context.pageType === PageTypeEnum.PDP

  const handleValueChange = (value: string) => {
    const index = tabs.findIndex(tab => tab.title === value)
    if (value !== selectedValue) {
      setSelectedValue(value)
      onChanged?.(tabs[index], index)
    }
  }

  const layoutTopMarginValue = isMobile ? 12 : 8
  const noDiscountMargin = isMobile && hasTitleAboveTabs ? -7 : 0
  const layoutTopMargin = hasDiscounts ? layoutTopMarginValue : noDiscountMargin

  return (
    <Box data-testid={testId} marginTop={layoutTopMargin}>
      <StyledTabs value={selectedValue} onValueChange={handleValueChange}>
        <TabList variant="chip">
          {tabs.map((tab, index) => {
            const showPromoTag = index === 1 && hasDiscounts
            const Trigger = showPromoTag ? PromoTabTrigger : Tab
            const strip = showPromoTag ? (
              <StyledInfoStrip
                selected={selectedValue === tab.title}
                isMobile={isMobile}
              >
                <IllustrationDiscount height={15} />
                <Flex
                  paddingTop={'3px'}
                  typography={'typeMobileDisplay06'}
                  color={'colorPillMembership'}
                >
                  {localiseText('multicard.multi_buy_promo_tag_text')}
                </Flex>
              </StyledInfoStrip>
            ) : null

            return (
              <Trigger key={tab.title} value={tab.title} aria-label={tab.label}>
                {tab.title}
                {strip}
              </Trigger>
            )
          })}
        </TabList>
        <StyledTabPanels>
          {tabs.map(tab => (
            <TabPanel key={tab.title} value={tab.title}>
              <Box>
                {tab.modules.map((m, key) =>
                  mapModule(m as Module, { ...context, key }),
                )}
              </Box>
            </TabPanel>
          ))}
        </StyledTabPanels>
      </StyledTabs>
    </Box>
  )
}
